import { ViCoreWarning, ViCoreError } from ".";
import type {
  ViCoreCarModel as ViCarModel,
  ViCorePartIdentifiers as ViPartIdentifiers,
  ViCoreImagePartDamage,
  ViCoreImageState,
} from "./";
import { ViCoreImageDamage, ViCoreImagePart } from "./ViCore";

export type { ViCarModel, ViPartIdentifiers };

export interface ViUploadedImage {
  id: string; // identical to localId until overwritten with image id received in response to image upload
  localId: string;
  status: ViImageStatus;
  filename: string;
  data: string; //base64 encoded image data
}

export interface ViAggregatedPartDamage {
  id: string;
  damageType: ViDamageType;
  partIdentifiers: ViPartIdentifiers;
  relativeSize: number;
  p: number;
  imagePartDamages: ViImagePartDamage[];
  claimDamagedPart: ViClaimDamagedPart;
}

export interface ViWorkAction {
  workAction: ViWorkActionLabel;
  p: number;
  isPrimary: boolean;
  operations: {
    operationId: ViOperationId;
    p: number;
  }[];
}

export interface ViPhysicalPart {
  id: string;
  standardGuideNumber: string;
  genericPartName: string;
  objectDetectionLabels?: string[];
  p: number;
  position: VIPhsyicalPartPosition;
  workActionResults: ViWorkAction[];
  consideredImages: [
    {
      id: string;
      importance: number;
    }
  ];
}

export interface VIPhsyicalPartPosition {
  code: string;
  p: number;
}

export interface ViClaimDamagedPart {
  id: string;
  partGroupName: string;
  objectDetectionLabels?: string[];
  physicalParts: ViPhysicalPart[];
  sideResult: {
    side: ViSide;
    p: number;
  } | null;
  p: number;
  aggregatedPartDamages: ViAggregatedPartDamage[];
}

export interface ViClaimTotalLoss {
  totalLoss: ViTotalLossLabel;
  p: number;
}

type ImageState = ViCoreImageState | ViImageStatus;

export interface ViImageData {
  id: string;
  localId: string;
  status: ImageState;
  filename: string;
  category?: { label: ViImageCategory; p: number };
  dimensions?: { width: number; height: number };
  damages?: ViImagePartDamage[];
  warnings?: ViCoreWarning[];
  error?: ViCoreError;
  data: string;
  isCompressed?: boolean;
  isDuplicate?: boolean;
}

export interface ViImagePartDamage
  extends Omit<
    ViCoreImagePartDamage,
    "imagePartId" | "imageDamageId" | "aggregatedPartDamageId"
  > {
  imagePart: ViImagePart;
  imageDamage: ViImageDamage;
  aggregatedPartDamage: ViAggregatedPartDamage;
}

export interface ViImagePart {
  id: string;
  imageId: string;
  bbox: [number, number][];
  contour: [number, number][];
  identifiers: ViPartIdentifiers;
  p: number;
}

export interface ViImageDamage {
  id: string;
  imageId: string;
  bbox: [number, number][];
  contour: [number, number][];
  p: number;
  type: ViDamageType;
}

export interface ViProcessingResult {
  processingId?: string;
  aggregatedPartDamages: ViAggregatedPartDamage[];
  claimDamagedParts: ViClaimDamagedPart[];
  totalLoss?: ViClaimTotalLoss;
  carModel?: ViCarModel;
  images?: ViImageData[];
  timing?: number;
}

export interface ViProcessingImage {
  id: string;
  results: {
    imagePartDamages: ViCoreImagePartDamage[];
    imageParts: ViCoreImagePart[];
    imageDamages: ViCoreImageDamage[];
  };
}

export enum ViImageStatus {
  "LOADING" = "LOADING",
  "COMPRESSING" = "COMPRESSING",
  "LOADED" = "LOADED",
  "PROCESSING" = "PROCESSING",
  "ACTIVE" = "ACTIVE",
  "INACTIVE" = "INACTIVE",
  "REJECTED" = "REJECTED",
  // "ERROR" = "ERROR",
}

export enum ViTotalLossLabel {
  TOTAL_LOSS = "total_loss",
  REPAIRABLE = "repairable",
  NEEDS_INSPECTION = "needs_inspection",
}

export enum ViDamageType {
  SCRATCH = "SCRATCH",
  DENT = "DENT",
  CRACK = "CRACK",
  BREAK = "BREAK",
  GLASS = "GLASS",
  MISSING = "MISSING",
}

export enum ViGenericPartName {
  FRONT_BUMPER = "FRONT_BUMPER",
  FRONT_DOOR = "FRONT_DOOR",
  FRONT_DOOR_WINDOW = "FRONT_DOOR_WINDOW",
  FRONT_FENDER = "FRONT_FENDER",
  HEADLIGHT = "HEADLIGHT",
  HOOD = "HOOD",
  REAR_BUMPER = "REAR_BUMPER",
  REAR_DOOR = "REAR_DOOR",
  REAR_DOOR_WINDOW = "REAR_DOOR_WINDOW",
  REAR_WINDSCREEN = "REAR_WINDSCREEN",
  ROOF = "ROOF",
  SIDE_MIRROR = "SIDE_MIRROR",
  TAILGATE = "TAILGATE",
  TAILLIGHT = "TAILLIGHT",
  WHEEL = "WHEEL",
  FRONT_WHEEL = "FRONT_WHEEL",
  REAR_WHEEL = "REAR_WHEEL",
  WINDSCREEN = "WINDSCREEN",
  WING = "WING",
  FOG_LIGHT = "FOG_LIGHT",
  GRILLE = "GRILLE",
  FRONT_BUMPER_MOULDING = "FRONT_BUMPER_MOULDING",
  REAR_BUMPER_MOULDING = "REAR_BUMPER_MOULDING",
  FRONT_DOOR_MOULDING = "FRONT_DOOR_MOULDING",
  REAR_DOOR_MOULDING = "REAR_DOOR_MOULDING",
}

export enum ViSide {
  NONE = "NONE",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export enum ViImageCategory {
  "CLOSEUP" = "CLOSEUP",
  "FULLVIEW" = "FULLVIEW",
  "DOCUMENT" = "DOCUMENT",
  "VIN" = "VINS",
  "ODOMETER" = "ODOMETER",
  "DISASSEMBLED" = "DISASSEMBLED",
  "INTERIOR" = "INTERIOR",
  "MECHANICAL" = "MECHANICAL",
  "UNRECOGNIZABLE" = "UNRECOGNIZABLE",
}

export enum ViWorkActionLabel {
  REPLACE = "REPLACE",
  REPAIR = "REPAIR",
  PAINT = "PAINT",
  "REMOVE-REFIT" = "REMOVE-REFIT",
  OTHER = "OTHER",
}

export enum ViOperationId {
  E = "E", // "REPLACE",
  L = "L", // "SURFACE PAINT",
  EC = "EC", // "REPLACE - ECONOMY PART",
  I = "I", // "REPAIR",
  BR = "BR", // "BLEND REFINISH",
  H = "H", // "HOLLOW CAVITY SEALING",
  LE = "LE", // "PAINT - NEW PART",
  N = "N", // "REMOVE & REFIT",
  M = "M", // "MAINTENANCE",
  LI1 = "LI1", // "REPAIR PAINT (>50%)",
  LE1 = "LE1", // "PAINT - NEW PART (PLASTIC)",
  D = "D", // "DENT REPAIR",
  LI = "LI", // "REPAIR PAINT",
  LS = "LS", // "SPOT PAINT",
  P = "P", // "VISUAL CHECK",
}
